import chunk from 'lodash.chunk';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import {
  TwoWayCommunicationService,
  isTwoWayApiConfigured,
} from '../../services/twoWayCommunicationService';

export type Conversation = {
  recipient: { customerId: string },
  conversationStatus?: string,
  activeFlowStatus?: string,
  needsHelpMessageIds?: Array<string>,
};

const RECIPIENT_IDS_PER_REQUEST = 100;
const NO_CONVERSATIONS = [];

/**
 * React Query counterpart of useFetchConversations. Each set of recipient ids
 * is its own query, so a response for an earlier set can never replace the
 * current one, and the in-flight request is aborted once its set is replaced.
 */
const useConversationsQuery = ({
  recipientIds,
  enabled = true,
}: {
  recipientIds: Array<string>,
  enabled?: boolean,
}): { conversations: Array<Conversation>, isLoading: boolean } => {
  const { data, isLoading } = useQuery(
    ['conversationsByRecipientIds', recipientIds],
    async ({ signal }) => {
      const twoWayCommunicationService = new TwoWayCommunicationService();
      const responses = await Promise.all(
        chunk(recipientIds, RECIPIENT_IDS_PER_REQUEST).map(
          (recipientIdsChunk) =>
            twoWayCommunicationService.getConversationsByRecipientsIds(
              recipientIdsChunk,
              { signal },
            ),
        ),
      );
      return responses.flat();
    },
    {
      enabled: Boolean(
        isTwoWayApiConfigured() && enabled && recipientIds.length,
      ),
      refetchOnWindowFocus: false,
      retry: false,
      onError: (error) => {
        toastr.error(
          'Error',
          error.networkError ||
            error.toString() ||
            'Failed to load conversations',
        );
      },
    },
  );

  return { conversations: data ?? NO_CONVERSATIONS, isLoading };
};

export default useConversationsQuery;
